import { STATUS_META } from 'app/pages/Apartment/Procurement/constants'

import { linkCell } from './exportUtils'

// ONE purchase-order document for the At Apartment flow, shared by the
// requests grid and the HQ daily sheet (user 2026-08-26: "po will be generated
// as full, but we will filter out the vendor and click generate po, then only
// those line items should be generated in pdf, the number maybe same and
// mentioned that it is part of the full list, 23/30 lines here").
//
// The PO number always stays the REQUEST's. When the rows handed in are only
// part of a request (a vendor filter, a day, a tick selection), the document
// says so — "23 of 30 lines on this PO", what filtered it, and the full PO's
// totals under the subset's — so a vendor holding the paper knows it is a cut,
// not the whole order. Sl No. is the line's number on the full PO, not its
// position in the filtered view, so "line 23" means the same row on every copy.
//
// File links stay REAL hyperlinks (linkCell → a clickable /URI annotation in
// the PDF), the capexPo.js pattern.

// A zero here means "not priced yet", and printing 0.00 in an order reads as
// free. Blank and zero both render as an em dash.
const numOrDash = (v) => {
  const n = Number(v)
  if (v == null || v === '' || !Number.isFinite(n) || n === 0) return '—'
  return n.toLocaleString('en-IN')
}

// Only the columns a vendor or an approver reads; the repeating ones live in
// the meta band. ⛔ NO MONEY ON A PO (user 2026-09-21: "in PO we will not
// mention the PP and other costs here, as we would need confirmation from the
// vendor, so all PO will have cost as non existant") — no rate, taxable, GST
// or invoice column, and no money in the totals. Our internal rates live on
// the grids and the Excel dump; the PO asks for work, it does not price it.
// ⭐ A line's searchable identity (user 2026-09-21: "mention a unique number
// that can be searched easily later like daily sheet date and sl number as a
// code"). The day the line belongs to on the register + its number on the PO,
// e.g. 21-09-2026/007 — the same string is written into the Google sheet's
// Reference ID column, so one search finds the line in the PDF, in Alfred and
// in the register.
export const lineCode = (row, i = 0) => {
  const d = String(row.sheet_date || (row.request || {}).created || '').slice(0, 10)
  const day = d.includes('-') ? d.split('-').reverse().join('-') : ''
  const n = String(row.sl_no || i + 1).padStart(3, '0')
  return day ? `${day}/${n}` : n
}

// A line can carry TWO files — a print job read off a mail brings two PDFs for
// one centre, and a sheet-imported line keeps its File in file_link alone. The
// daily sheet grid shows both; the PO has to as well, or the printer gets half
// the job. One link stays "open file"; two stack as "file 1" / "file 2", each
// its own hyperlink.
const fileCell = (r) => {
  const links = [...new Set([r.reference_link, r.file_link].filter(Boolean))]
  if (links.length < 2) return linkCell(links[0], 'open file')
  return {
    text: links.map((_, i) => `file ${i + 1}`).join('\n'),
    link: links[0],
    links
  }
}

export const APT_PO_COLS = [
  { label: '#', pick: (r, i) => String(r.sl_no || i + 1), right: true },
  { label: 'Code', pick: (r, i) => lineCode(r, i) },
  {
    label: 'Product',
    pick: (r) =>
      [r.item_name || '', r.reference_id ? `(${r.reference_id})` : '']
        .filter(Boolean)
        .join(' ')
  },
  { label: 'Print specification', pick: (r) => r.print_specification || '' },
  { label: 'Paper', pick: (r) => r.paper_kind || '' },
  { label: 'File', pick: (r) => fileCell(r) },
  {
    label: 'Sides/file',
    pick: (r) => numOrDash(r.sides_per_file),
    right: true
  },
  { label: 'Units', pick: (r) => numOrDash(r.qty), right: true },
  { label: 'Total sides', pick: (r) => numOrDash(r.total_sides), right: true }
]

const sumOf = (rows, key) =>
  rows.reduce((t, r) => {
    const n = Number(r[key])
    return t + (Number.isFinite(n) ? n : 0)
  }, 0)

// A PO that covers more than one centre needs to say which line is whose —
// 130 "<child> — Learning Passport" rows are otherwise unsortable at the
// printer's end (user 2026-09-21). Only then does the column appear.
const CENTRE_COL = { label: 'Centre', pick: (r) => r.centre_label || '' }
const colsFor = (multiCentre) => {
  if (!multiCentre) return APT_PO_COLS
  const at = APT_PO_COLS.findIndex((c) => c.label === 'Product') + 1
  return [...APT_PO_COLS.slice(0, at), CENTRE_COL, ...APT_PO_COLS.slice(at)]
}

// Built from the columns in play, so an optional column can never shift the
// numbers out from under their headers. The label rides in the Product column
// — '#' and 'Code' are too narrow for "Total (4 lines)" and wrap it three deep.
const totalsRow = (cols, rows, label) =>
  cols.map((c) => {
    if (c.label === 'Product') return label
    if (c.label === 'Units') return numOrDash(sumOf(rows, 'qty'))
    if (c.label === 'Total sides') return numOrDash(sumOf(rows, 'total_sides'))
    return ''
  })

// One value when every row agrees, else ''.
const uniq = (rows, key) => {
  const set = new Set(rows.map((r) => r[key]).filter(Boolean))
  return set.size === 1 ? [...set][0] : ''
}

// The full line list a PO covers: lines HQ removed are off the order.
const fullLinesOf = (request) =>
  (request.lines || []).filter((l) => !l.removed_by_hq)

// Build one renderOrder spec per request represented in `rows` (the filtered /
// ticked sheet rows). Requests keep first-appearance order — the register is
// hand-ordered, never re-sort it. `filterNote` is a human line naming what cut
// the view (e.g. "Vendor: Anmol · 26 Aug 2026"); it prints only on a subset.
export const apartmentPoSpecs = ({ rows, filterNote }) => {
  const byRequest = new Map()
  ;(rows || []).forEach((row) => {
    const req = row.request || {}
    const key = req.uuid || row.po_number || 'unattached'
    if (!byRequest.has(key)) byRequest.set(key, { request: req, rows: [] })
    byRequest.get(key).rows.push(row)
  })

  return [...byRequest.values()].map(({ request, rows: subset }) => {
    const full = fullLinesOf(request)
    const isSubset = full.length > subset.length
    const po = request.po_number || ''
    const categories = [
      ...new Set(subset.map((r) => r.category).filter(Boolean))
    ]
    const kinds = [...new Set(subset.map((r) => r.item_type || 'goods'))]
    const vendors = [...new Set(subset.map((r) => r.vendor).filter(Boolean))]
    // ⭐ The document names its own source (user 2026-09-21, on a print job for
    // three centres that printed "At Apartment · Purchase Order · HQ daily
    // sheet": "this is not At Apt, why is it mentioned in the PO?"). Only a
    // request that belongs to an apartment is an At Apartment order; everything
    // else on the daily sheet — a mail forward, an HQ direct entry — is a
    // centre order, and the centres are named instead of the register.
    const apartment = request.apartment_name || uniq(subset, 'apartment_name')
    const centres = [
      ...new Set(subset.map((r) => r.centre_label).filter(Boolean))
    ]
    const multiCentre = !apartment && centres.length > 1
    const cols = colsFor(multiCentre)

    return {
      filename: po
        ? `${String(po).replace(/[\\/:*?"<>|]/g, '-')}-order`
        : 'purchase-order',
      title: apartment
        ? 'At Apartment · Purchase Order'
        : 'Openhouse · Purchase Order',
      subtitle: categories.join(' · '),
      po,
      meta: [
        apartment
          ? { k: 'Apartment', v: apartment }
          : {
              k: centres.length > 1 ? 'Centres' : 'Centre',
              // The meta band is one column wide — a long list runs straight
              // over the Vendor cell (seen on AA-PO-0033's seven centres), so
              // past three it becomes a count and the Centre column carries
              // the detail.
              v:
                centres.length > 3
                  ? `${centres.length} centres`
                  : centres.join(' · ') || 'HQ'
            },
        {
          k: 'Vendor',
          v:
            vendors.length > 1
              ? `Mixed (${vendors.length})`
              : vendors[0] || 'Not assigned'
        },
        {
          k: 'Raised on',
          v: String(request.created || uniq(subset, 'sheet_date') || '').slice(
            0,
            10
          )
        },
        { k: 'Requested by', v: request.requested_by_name || '' },
        {
          k: 'Status',
          v: (STATUS_META[request.status] || {}).label || request.status || ''
        },
        {
          k: 'Lines',
          v: `${subset.length}${isSubset ? ` of ${full.length}` : ''} ${
            kinds.length === 1 ? kinds[0] : 'mixed'
          }`
        }
      ],
      note: isSubset
        ? `Part of the full list — ${subset.length} of ${full.length} lines on this PO` +
          (filterNote ? ` · filtered by ${filterNote}` : '')
        : '',
      headers: cols.map((c) => c.label),
      align: cols.map((c) => (c.right ? 'right' : 'left')),
      rows: subset.map((r, i) => cols.map((c) => c.pick(r, i))),
      totals: totalsRow(
        cols,
        subset,
        isSubset ? `Total (${subset.length} lines)` : 'Total'
      ),
      postNote: isSubset
        ? `Full PO ${po || ''}: ${full.length} lines · ${numOrDash(
            sumOf(full, 'qty')
          )} units · ${numOrDash(sumOf(full, 'total_sides'))} total sides`
        : '',
      footNote:
        'Generated from Alfred · Rates and taxes to be confirmed by the vendor'
    }
  })
}
